import { Component, EventEmitter, HostBinding, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as cloneDeep from 'lodash/cloneDeep';

import { ConfirmationService, MessageService, ScrollerOptions } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';

import { WaitingIndicatorComponent } from '../../waiting-indicator/waiting-indicator.component';
import { PurchaseOrderManagerService } from '../../../services/purchase-order/purchase-order-manager.service';
import { DeviceManagerService } from '../../../core/services/device-manager.service';
import { PurchaseOrderLineItem, PurchaseOrderLineItemMiscellaneous } from '../../../services/purchase-order/purchase-order-line-item.model';
import { PurchaseOrderTransactionPartType } from '../../../services/purchase-order/purchase-order-transaction';
import { PricingType } from '../../../services/miscellaneous-part/miscellaneous-part.model';
import { VendorActivityStatus } from '../../../services/vendor/vendor.model';
import { VendorManagerService } from '../../../services/vendor/vendor-manager.service';
import { AuthManagerService } from '../../../services/auth/auth-manager.service';
import { PurchaseOrderPDFComponent, SignaturePageType } from '../purchase-order-pdf/purchase-order-pdf.component';
import { LocalCacheManager } from '../../../managers/local-cache.manager';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { ThemeManager } from '../../../managers/theme.manager';
import { AssignedImageResult } from '../../../services/converter/assigned-image-result.model';
import { ImageApiService } from '../../../shared-api/images/image-api.service';

import * as moment from 'moment/moment';
import { EnvUtils } from 'src/app/shared-ui/utils/environment-utils';
import { DecimalPipe } from '@angular/common';
import { BatchManagerService } from 'src/app/services/batch/batch-manager.service';
import { Batch, BatchStatus } from 'src/app/services/batch/batch.model';
import { NgxImageCompressService, UploadResponse } from 'ngx-image-compress';
import { ILogger } from 'src/app/services/logger/logger.service';
import { PurchaseOrderMetals } from 'src/app/services/purchase-order/purchase-order-metals.model';
import { ExistingPoVendorListItemModel } from 'src/app/services/purchase-order/models/exising-po-vendor-list-item.model';
import { HedgeApiService } from 'src/app/services/hedge/hedge-api.service';
import { HedgeForVendorListItemModel } from 'src/app/services/hedge/models/hedge-for-vendor-list-item.model';
import { openBlobUrl } from 'src/app/common/guards/blob-url-guard';

@Component({
    selector: 'ls-purchase-order',
    templateUrl: './purchase-order.component.html',
    styleUrls: [ './purchase-order.component.less' ],
    animations: WaitingIndicatorComponent.animations_no_fade,
})
export class PurchaseOrderComponent implements OnInit, OnDestroy
{
    static isDisplayed = false;
    static ONE_LINE_TABLE_HEIGHT = 105;
    static TWO_LINE_TABLE_HEIGHT = 142;

    @HostBinding('class') get classes(): string { return ThemeManager.theme; }
    @ViewChild('fileUpload', { static: false }) fileUpload: FileUpload;

    @ViewChild('pdf', { static: false }) pdf: PurchaseOrderPDFComponent;
    @Output() hide = new EventEmitter();
    sidebarVisible: boolean = false;
    isEuLaunch = EnvUtils.isEuRegion;
    poNotes: string;
    busy = false;
    error: string;
    convertersDisplayed = false;
    // customDisplayed = false;
    editedItemCopy: PurchaseOrderLineItem | PurchaseOrderLineItemMiscellaneous;
    gettingSignature = false;
    imageHoverIndex: number;
    headerDisplayed = true;
    levelOptions = PurchaseOrderLineItem.levelPercentageDropdownOptions;
    maxPurchaseDate = moment().endOf('day').toDate();
    miscDisplayed = false;
    adminEditPageType: SignaturePageType;
    selectedVendor: ExistingPoVendorListItemModel;
    signaturePageTypeSelected: SignaturePageType;

    vendorsFilterValue: string;
    vendors: ExistingPoVendorListItemModel[];
    vendorListLoading: boolean = false;
    pageSize: number = 20;
    pageIndex: number = 0;
    searchString: string = null;
    totalItems: number;
    vendorsScrollerOptions: ScrollerOptions = {
        showLoader: false,
        lazy: true,
        onLazyLoad: this.onLazyLoad.bind(this)
    };

    updateNotesVisible: boolean = false;
    selectedBatch: number;
    batches: Batch[];
    batchesLoading = false;
    batchesPageSize = 20;
    batchesScrollerOptions: ScrollerOptions = {
        showLoader: false,
        lazy: true,
        onLazyLoad: this.onBatchesLazyLoad.bind(this)
    };
    private batchesLastPageWasFull = true;
    private batchesNextPage = 0;
    batchesFilterValue: string;
    private batchesSearchString: string = null;
    metals: PurchaseOrderMetals | null = null;

    availableHedges: HedgeForVendorListItemModel[] = [];
    selectedHedge: HedgeForVendorListItemModel | null = null;
    hedgesLoading = false;
    hedgesPageSize = 20;
    hedgesTotalItems: number = 0;
    hedgesFilterValue: string;
    hedgesSearchString: string = null;
    hedgesScrollerOptions: ScrollerOptions = {
        showLoader: false,
        lazy: true,
        onLazyLoad: this.onHedgesLazyLoad.bind(this)
    };

    converterColumns: any[] = [
        { field: 'converterPartNumber', header: 'Part #', width: '10%' },
        { field: 'converterGroupName', header: 'Manufacturer', width: '10%' },
        { field: 'quantity', header: 'Quantity', width: '10%', style: { 'text-align': 'center' } },
        { field: 'levelPercentageText', header: 'Metal Quantity', width: '10%', style: { 'text-align': 'center' } },
        { field: 'pricePerItem', header: 'Price / item', width: '10%', style: { 'text-align': 'center' } },
        { field: 'totalPrice', header: 'Total Price', width: '10%', style: { 'text-align': 'center' } }
    ];

    customColumns: any[] = [
        { field: 'converterGroupName', header: 'Manufacturer', width: '10%' },
        { field: 'quantity', header: 'Quantity', width: '10%', style: { 'text-align': 'center' } },
        { field: 'pricePerItem', header: 'Price / item', width: '10%', style: { 'text-align': 'center' } },
        { field: 'totalPrice', header: 'Total Price', width: '10%', style: { 'text-align': 'center' } }
    ];

    categoryPartColumns: any[] = [
        { field: 'converterGroupName', header: 'Manufacturer', width: '10%' },
        { field: 'quantity', header: 'Quantity', width: '10%', style: { 'text-align': 'center' } },
        { field: 'levelPercentageText', header: 'Metal Quantity', width: '10%', style: { 'text-align': 'center' } },
        { field: 'pricePerItem', header: 'Price / item', width: '10%', style: { 'text-align': 'center' } },
        { field: 'totalPrice', header: 'Total Price', width: '10%', style: { 'text-align': 'center' } }
    ];

    miscColumns: any[] = [
        { field: 'pricingType', header: 'Type', width: '15%' },
        { field: 'description', header: 'Description', width: '20%' },
        { field: 'quantity', header: 'Quantity', width: '10%', style: { 'text-align': 'center' } },
        { field: 'pricePerItem', header: 'Price / item', width: '10%', style: { 'text-align': 'center' } },
        { field: 'totalPrice', header: 'Total Price', width: '10%', style: { 'text-align': 'center' } }
    ];

    attachmentTypes = [
        { friendlyName: 'Material', value: 'MAT'},
        { friendlyName: 'Vehicle', value: 'VEH'},
        { friendlyName: 'Seller', value: 'SEL'},
        { friendlyName: 'License', value: 'LIC'},
        { friendlyName: 'Other', value: 'OTH'},
    ]

    signaturePageMenuItemsUs = [
        { label: 'California', type: SignaturePageType.California, command: null },
        { label: 'Illinois', type: SignaturePageType.Illinois, command: null },
        { label: 'Missisipi', type: SignaturePageType.Missisipi, command: null },
        { label: 'Texas', type: SignaturePageType.Texas, command: null },
        { label: 'West Virginia', type: SignaturePageType.WestVirginia, command: null },
        { label: 'Default', type: SignaturePageType.Default, command: null },
    ];

    allPrintMenuItems = [
        { label: 'Send Summary to Vendor', command: null, items: null, emailToVendor: true, showDetails: false, wordDoc: false },
        { label: 'Send Detailed to Vendor', command: null, items: null, emailToVendor: true, showDetails: true, wordDoc: false },
        { label: 'Create Summary PDF', command: null, items: null, emailToVendor: false, showDetails: false, wordDoc: false },
        { label: 'Create Detailed PDF', command: null, items: null, emailToVendor: false, showDetails: true, wordDoc: false },
        //{ label: 'Get Detailed Word Document', command: null, items: null, emailToVendor: false, showDetails: true, wordDoc: true },
        { label: 'Create Quote', command: null, items: null, emailToVendor: false, showDetails: true, wordDoc: false, quote: true },
    ];

    allPrintMenuItemsEu = [
        { label: 'Send Summary to Vendor', command: null, items: null, emailToVendor: true, showDetails: false, wordDoc: false },
        { label: 'Send Detailed to Vendor', command: null, items: null, emailToVendor: true, showDetails: true, wordDoc: false },
        { label: 'Create Summary PDF', command: null, items: null, emailToVendor: false, showDetails: false, wordDoc: false },
        //{ label: 'Get Detailed Word Document', command: null, items: null, emailToVendor: false, showDetails: true, wordDoc: true },
    ];
    printMenuItems: any[];

    PricingType = PricingType;
    PurchaseOrderTransactionPartType = PurchaseOrderTransactionPartType;
    LocalCacheManager = LocalCacheManager;
    ThemeManager = ThemeManager;
    ImageApiService = ImageApiService;

    private emailToVendorAfterSignature: boolean;
    private originalPurchaseDate: Date;
    private showDetailsAfterSignature: boolean;
    private generateWordDocAfterSignature: boolean;
    
    constructor(private router: Router,
                public authManager: AuthManagerService,
                private confirmationService: ConfirmationService,
                public deviceManager: DeviceManagerService,
                private messageService: MessageService,
                public poManager: PurchaseOrderManagerService,
                private batchManager: BatchManagerService,
                private hedgeApiService: HedgeApiService,
                private zone: NgZone,
                private imageCompress: NgxImageCompressService,
                private log: ILogger)
    {
        if(!authManager.currentUser.isGlobalReader){
            this.converterColumns.push({ field: 'editColumn', header: '', width: '10%', style: { 'text-align': 'center' }, sortDisabled: true });
            this.customColumns.push({ field: 'editColumn', header: '', width: '10%', style: { 'text-align': 'center' }, sortDisabled: true });
            this.categoryPartColumns.push({ field: 'editColumn', header: '', width: '10%', style: { 'text-align': 'center' }, sortDisabled: true });
            this.miscColumns.push({ field: 'editColumn', header: '', width: '10%', style: { 'text-align': 'center' }, sortDisabled: true });
        }
    }
    ngOnInit(): void
    {
        this.seletedAttachmentType = this.attachmentTypes[0].value;
        this.authManager.currentUserChanged.subscribe(() => PurchaseOrderComponent.isDisplayed = false);

        this.loadBatchesPage(0);

        this.vendors = [
            {
                name: this.poManager.current.vendor.name,
                activityStatus: this.poManager.current.vendor.activityStatus,
                address: this.poManager.current.vendor.address,
                city: this.poManager.current.vendor.city,
                state: this.poManager.current.vendor.state,
                zip: this.poManager.current.vendor.zip,
                id: this.poManager.current.vendor.id,
                hasCompleteData: this.poManager.current.vendor.hasCompleteData,
                hasCompleteLicenseData: this.poManager.current.vendor.hasCompleteLicenseData,
                hasValidContact: this.poManager.current.vendor.contacts.some(x => x.isContactValidForPO)
            }
        ]
        this.selectedVendor = this.vendors[0];

            const numSectionsDisplayed = this.convertersDisplayed ? 1 : 0 + (this.miscDisplayed ? 1 : 0);
            const totalLineItems = this.poManager.current.lineItemsConverter.length + this.poManager.current.lineItemsCustom.length +
                this.poManager.current.lineItemsNoNumber.length + this.poManager.current.lineItemsCategory.length +
                this.poManager.current.lineItemsMiscellaneous.length;
            this.convertersDisplayed = this.miscDisplayed =
                this.deviceManager.isDesktop || numSectionsDisplayed === 1 || totalLineItems < 5;
            PurchaseOrderComponent.isDisplayed = true;

            // Add signature page options to each Print option
            this.setupPrintMenu();

            this.originalPurchaseDate = this.poManager.current.purchaseDate;

            if(!this.updateNotesVisible)
                this.updateNotesVisible = this.poManager.current.notes != null;

            this.selectedBatch = this.poManager.current.batchId;

            this.loadHedgesForVendor(this.poManager.current.vendorId);
    }
    
    menuOnShow(): void {
        if(this.authManager.currentUser.vendorId != null)
            return;

        this.printMenuItems
            .filter(x => !x.quote)
            .forEach(i => i.disabled = !this.poManager.current.hasCompleteData && !this.poManager.current.isLocked)
    }

    ngOnDestroy(): void {
        PurchaseOrderComponent.isDisplayed = false;
    }

    //region PO-level operations
    changeVendor(event: any): void
    {
        if (this.selectedVendor.id === this.poManager.current.vendor.id) 
            return;

        const userExempt = this.authManager.currentUser.exemptFromVendorLicenseRequirements;
        if ((userExempt && !event.value.hasCompleteData && event.value.hasCompleteLicenseData) || (!userExempt && !event.value.hasCompleteData))
        {
            this.confirmationService.confirm({
                message: 'This vendor does not have complete information.\n\nPlease go to the Vendors list and update the vendor information before assigning that vendor to a PO.',
                header: 'Vendor Information Incomplete',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'OK',
                rejectVisible: false
            });
            //setTimeout(() => { this.selectedVendor = event; }, 10);
            // return;
        }

        this.busy = true;
        this.poManager.changeVendor(event.value.id).pipe(finalize(() =>
        {
            this.busy = false;
            this.selectedHedge = null;
            this.loadHedgesForVendor(event.value.id);
            if (this.vendors.find(v => v.id === event.value).hasValidContact)
            {
                this.confirmationService.confirm({
                    message: `There is at least one contact with invalid driver's license information.`,
                    header: 'Contact Information Incomplete',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'OK',
                    rejectVisible: false
                });
            }
        })).subscribe();
    }

    allowAlphanumeric(event: KeyboardEvent) {
      if (!/^[a-zA-Z0-9-]$/.test(event.key)) {
        event.preventDefault();
      }
    }
    onBatchSave(){
        this.busy = true;
        this.poManager.saveBatchNumber(this.selectedBatch).subscribe(() => {
            this.busy = false;
            this.poManager.current.batchId = this.selectedBatch;
            this.poManager.current.batchNumber = this.batches.find(x => x.id == this.selectedBatch)?.number ?? null;
            this.poManager.purchaseOrderUpdated.next(this.poManager.current)
        },(error) => {
            this.busy = false;
            this.messageService.add({
                    severity: 'error',
                    summary: `Set batch/load number`,
                    detail: ErrorHandlerService.handleError(this, error, 'onBatchSave()', false, false)
                });
        })

    }
    loadHedgesForVendor(vendorId: number): void {
        if (!vendorId) return;
        this.hedgesSearchString = null;
        this.hedgesFilterValue = undefined;
        this.hedgesLoading = true;
        this.availableHedges = [];
        this.loadHedgesPage(vendorId, 0);
    }

    onFilterHedges(): void {
        this.hedgesSearchString = this.hedgesFilterValue;
        this.loadHedgesPage(this.poManager.current.vendorId, 0);
    }

    onClearHedgesFilter(): void {
        if(this.hedgesSearchString == null)
            return;
        this.hedgesSearchString = null;
        this.hedgesFilterValue = undefined;
        this.loadHedgesPage(this.poManager.current.vendorId, 0);
    }

    private loadHedgesPage(vendorId: number, page: number): void {
        this.hedgeApiService.getHedgesForVendorV2(vendorId, {
            pageIndex: page,
            pageSize: this.hedgesPageSize,
            searchString: this.hedgesSearchString
        }).subscribe({
            next: res => {
                this.hedgesTotalItems = res.totalCount;
                if (page === 0) {
                    this.availableHedges = res.items;
                    this.selectedHedge = res.items.find(h => h.id === this.poManager.current.hedgeId) ?? null;
                } else {
                    this.availableHedges = [...this.availableHedges, ...res.items];
                }
                this.hedgesLoading = false;
            },
            error: () => { this.hedgesLoading = false; }
        });
    }

    onHedgesLazyLoad(event: any): void {
        if (this.availableHedges.length >= this.hedgesTotalItems) return;
        const { last } = event;
        if (last >= this.availableHedges.length && !this.hedgesLoading) {
            this.hedgesLoading = true;
            const nextPage = Math.floor(this.availableHedges.length / this.hedgesPageSize);
            this.loadHedgesPage(this.poManager.current.vendorId, nextPage);
        }
    }

    onFilterBatches(): void {
        this.batchesSearchString = this.batchesFilterValue;
        this.loadBatchesPage(0);
    }

    onClearBatchesFilter(): void {
        if(this.batchesSearchString == null)
            return;
        this.batchesSearchString = null;
        this.batchesFilterValue = undefined;
        this.loadBatchesPage(0);
    }

    private loadBatchesPage(page: number): void {
        this.batchesLoading = true;
        this.batchManager.getBatchesForLocation(this.authManager.currentUser.locationId, {
            pageIndex: page,
            pageSize: this.batchesPageSize,
            searchString: this.batchesSearchString
        }).subscribe({
            next: res => {
                const filtered = res.items.filter(x => x.status !== BatchStatus.Close);
                this.batchesLastPageWasFull = res.items.length === this.batchesPageSize;
                this.batchesNextPage = page + 1;
                if (page === 0) {
                    this.batches = filtered;
                } else {
                    this.batches = [...this.batches, ...filtered];
                }
                this.batchesLoading = false;
            },
            error: () => { this.batchesLoading = false; }
        });
    }

    onBatchesLazyLoad(event: any): void {
        if (!this.batchesLastPageWasFull || this.batchesLoading) return;
        const { last } = event;
        if (last >= this.batches.length) {
            this.loadBatchesPage(this.batchesNextPage);
        }
    }

    onHedgeChange(event: any): void {
        const hedgeId: number | null = event.value?.id ?? null;
        this.poManager.assignHedge(hedgeId).subscribe(() => {
            this.poManager.current.hedgeContractNumber = event.value?.number ?? null;
            this.messageService.add({ severity: 'success', summary: 'Hedge Contract', detail: hedgeId ? 'Hedge contract assigned.' : 'Hedge contract removed.' });
        }, () => {
            this.messageService.add({ severity: 'error', summary: 'Hedge Contract', detail: 'Could not update hedge contract.' });
        });
    }

    onUpdateNotes(notes: string) {
        this.poManager.updateNotes(notes).subscribe(() => {
            this.messageService.add({
                    severity: 'success',
                    summary: `Purchase Order Notes`,
                    detail: `The Purchase Order Notes has been updated.`
                });
        }, () => {
            this.messageService.add({
                    severity: 'error',
                    summary: `Purchase Order File`,
                    detail: `Cannot update notes.`
                });
        });
    }
    showMetals(): void {
        this.poManager.getMetalsToShow(this.poManager.current.id).subscribe(result => {
            this.metals = result;
        }, (error) => {
            this.messageService.add({
                    severity: 'error',
                    summary: `Show metals`,
                    detail: ErrorHandlerService.handleError(this, error, 'showMetals()', false, false)
                });
        })
    }
    setPurchaseDate(): void
    {
        this.confirmationService.confirm({
            message: 'This PO has been locked. Are you sure you want to update the date for a locked PO?',
            header: 'Update Locked PO?',
            icon: 'pi pi-exclamation-triangle',
            accept: () =>
            {
                this.setPurchaseDateInternal();
            },
            reject: () =>
            {
                this.poManager.current.purchaseDate = this.originalPurchaseDate;
            }
        });
    }

    private setPurchaseDateInternal(): void
    {
        this.originalPurchaseDate = this.poManager.current.purchaseDate;
        this.busy = true;
        this.poManager.setPurchaseDate(this.poManager.current.purchaseDate).pipe(finalize(() => this.busy = false)).subscribe();
    }

    deletePO(): void
    {
        this.confirmationService.confirm({
            message: 'This will permanently delete the PO. Do you want to continue?',
            header: 'Permanently Delete?',
            icon: 'pi pi-exclamation-triangle',
            accept: () =>
            {
                this.busy = true;
                this.poManager.deletePO(this.poManager.current.id).pipe(finalize(() => this.busy = false))
                    .subscribe(() => { this.closePO(); }, error =>
                    {
                        this.error = ErrorHandlerService.handleError(this, error, 'deletePO()', false, false);
                    });

            }
        });
    }

    addParts(): void
    {
        this.closeView();
        this.router.navigate([ '/converters/part-number' ]);
    }

    outputQuote(showDetails: boolean){
        this.busy = true;
        this.poManager.refreshCurrentPurchaseOrder().pipe(finalize(() => this.busy = false)).subscribe(() =>
        {
            this.pdf.outputQuote(showDetails);
        });
    }

    outputPDFAdminEdit(signaturePageType: SignaturePageType): void
    {
        // this.pdf.initializeBrowserWindow();
        this.emailToVendorAfterSignature = false;
        this.showDetailsAfterSignature = false;
        this.generateWordDocAfterSignature = false;
        
        this.gettingSignature = true;
                this.busy = true;
                this.poManager.refreshCurrentPurchaseOrder().pipe(finalize(() => this.busy = false)).subscribe(() =>
                {
                    this.pdf.captureSignature(signaturePageType, true);
                });

    }

    outputPDF(emailToVendor: boolean, showDetails: boolean, generateWordDoc: boolean, signaturePageType: SignaturePageType): void
    {
        // this.pdf.initializeBrowserWindow();
        this.signaturePageTypeSelected = signaturePageType;
        this.emailToVendorAfterSignature = emailToVendor;
        this.showDetailsAfterSignature = showDetails;
        this.generateWordDocAfterSignature = generateWordDoc;
        if (this.poManager.current.isLocked)
        {
            this.gettingSignature = true;
            this.pdf.captureSignature(signaturePageType);
            return;
        }

        this.confirmationService.confirm({
            message: 'This will finalize the PO and lock it from further changes. Do you want to continue?',
            header: 'Confirmation',
            icon: 'fa fa-question-circle',
            reject: () => { this.setupPrintMenu(); },
            accept: () =>
            {
                this.gettingSignature = true;
                this.busy = true;
                this.poManager.refreshCurrentPurchaseOrder().pipe(finalize(() => this.busy = false)).subscribe(() =>
                {
                    
                    this.poManager.current.purchaseDate = new Date();
                    this.pdf.captureSignature(signaturePageType);
                });
            }
        });
    }

    updatePrices(): void
    {
        this.busy = true;
        this.poManager.updatePrices().pipe(finalize(() => this.busy = false)).subscribe();
    }

    lock(): void
    {
        this.busy = true;
        this.poManager.lock(this.signaturePageTypeSelected, this.pdf.selectedContactId, this.signaturePageTypeSelected == SignaturePageType.WestVirginia ? this.pdf.wvCompliancePage.additionalData : null).pipe(finalize(() => this.busy = false)).subscribe();
    }

    refresh(): void
    {
        this.busy = true;
        this.poManager.retryTransactions().pipe(finalize(() => this.busy = false), switchMap(result =>
        {
            if (!result) return of(false);
            return this.poManager.getPurchaseOrder(this.poManager.current.id);
        })).subscribe();
    }

    unlock(): void
    {
        this.confirmationService.confirm({
            message: 'Are you sure you want to unlock this Purchase Order?',
            header: 'Unlock?',
            icon: 'pi pi-exclamation-triangle',
            accept: () =>
            {
                this.poManager.unlock().subscribe(() => {
                    this.setupPrintMenu()
                    this.poManager.setCurrentPO(this.poManager.current.id).subscribe();
                });
            }
        });
    }

    downloadPo(){
        this.pdf.downloadPo();
    }
    async onSignatureCompleted(complete: boolean): Promise<void>
    {
        this.gettingSignature = false;
        if (!complete) return;

        // If it is already locked we can simply generate it
        if (this.poManager.current.isLocked)
        {
            if (this.generateWordDocAfterSignature)
                this.generateWordDoc();
            else
                await this.pdf.create(this.emailToVendorAfterSignature, this.showDetailsAfterSignature);
            return;
        }

        // Not locked - lock it then proceed
        this.busy = true;
        this.poManager.updatePaymentType().subscribe(async () =>
        {
            this.poManager.setPurchaseDateDirectly().subscribe(async () => {
            if (!this.generateWordDocAfterSignature)
            {
                // Note: pdf.create will lock the PO when the signature page is uploaded
                await this.pdf.create(this.emailToVendorAfterSignature, this.showDetailsAfterSignature);
                this.busy = false;
                return;
            }

            this.poManager.lock(this.signaturePageTypeSelected, this.pdf.selectedContactId, this.signaturePageTypeSelected == SignaturePageType.WestVirginia ? this.pdf.wvCompliancePage.additionalData : null).pipe(finalize(() => this.busy = false)).subscribe(async () =>
                {
                    this.generateWordDoc();
                },
                error =>
                {
                    this.error = ErrorHandlerService.handleError(this, error, 'outputPDF()', false, false);
                });
                
            })
        },
        error =>
        {
            this.error = ErrorHandlerService.handleError(this, error, 'outputPDF()', false, false);
        });
    }

    private generateWordDoc(): void
    {
        this.poManager.outputPurchaseOrderAsWordDoc(this.poManager.current.id).subscribe(result => {
            const blob = new Blob([result], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${this.poManager.current.number}.docx`;
            link.click();
            this.setupPrintMenu();
        });
    }
    onDownloadAll() {
        this.busy = true;
        this.poManager.getImagesAsZip().subscribe(blob => {
            this.busy = false;
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement('a');
            a.href = url;
            a.click();
            window.URL.revokeObjectURL(url);
        }, error => {
            this.busy = false;
            this.messageService.add({
                severity: 'error',
                summary: `Images`,
                detail: `Cannot download images.`
            });
        })
    }
    onPdfGenerated(complete: boolean): void
    {
        this.busy = false;
        this.setupPrintMenu();
    }
    //endregion

    //region Row editing
    onRowEditInit(lineItem: PurchaseOrderLineItem | PurchaseOrderLineItemMiscellaneous): void
    {
        this.editedItemCopy = lineItem instanceof PurchaseOrderLineItem
            ? new PurchaseOrderLineItem(cloneDeep(lineItem))
            : new PurchaseOrderLineItemMiscellaneous(cloneDeep(lineItem));
    }

    onRowEditSave(arr: PurchaseOrderLineItem[] | PurchaseOrderLineItemMiscellaneous[],
                  lineItem: PurchaseOrderLineItem | PurchaseOrderLineItemMiscellaneous): void
    {
        if (lineItem.quantity === this.editedItemCopy.quantity &&
            lineItem instanceof PurchaseOrderLineItem &&
            lineItem.levelPercentage === (this.editedItemCopy as PurchaseOrderLineItem).levelPercentage)
        {
            this.editedItemCopy = null;
            return;
        }
        this.busy = true;
        if (lineItem instanceof PurchaseOrderLineItem)
        {
            this.poManager.updateLineItem(lineItem.id, lineItem.quantity, lineItem['levelPercentage'], lineItem.type).subscribe(() => {
                this.editedItemCopy = null;
                this.busy = false;
            })
            return;
        }
        this.poManager.updateLineItem(lineItem.id, lineItem.quantity, lineItem['levelPercentage'], PurchaseOrderTransactionPartType.Miscellaneous).subscribe(() => {
            this.editedItemCopy = null;
            this.busy = false;
        })
    }

    onRowEditCancel(arr: PurchaseOrderLineItem[] | PurchaseOrderLineItemMiscellaneous[]): void
    {
        const index = arr.findIndex(li => li.id === this.editedItemCopy.id);
        arr[index] = this.editedItemCopy;
        if (this.poManager.current.lineItemsAllConverters === arr) {
            this.poManager.current.lineItemsAllConverters = [ ...arr ];
            const copy = this.editedItemCopy as PurchaseOrderLineItem;
            let indexInOriginalList = this.poManager.current.lineItemsConverter
                .findIndex(li => li.id === this.editedItemCopy.id && li.type === copy.type);
            if (indexInOriginalList !== -1) this.poManager.current.lineItemsConverter[indexInOriginalList] = copy;
            indexInOriginalList = this.poManager.current.lineItemsNoNumber
                .findIndex(li => li.id === this.editedItemCopy.id && li.type === copy.type);
            if (indexInOriginalList !== -1) this.poManager.current.lineItemsNoNumber[indexInOriginalList] = copy;
            indexInOriginalList = this.poManager.current.lineItemsCategory
                .findIndex(li => li.id === this.editedItemCopy.id && li.type === copy.type);
            if (indexInOriginalList !== -1) this.poManager.current.lineItemsCategory[indexInOriginalList] = copy;
            indexInOriginalList = this.poManager.current.lineItemsCustom
                .findIndex(li => li.id === this.editedItemCopy.id && li.type === copy.type);
            if (indexInOriginalList !== -1) this.poManager.current.lineItemsCustom[indexInOriginalList] = copy;
        }
        if (this.poManager.current.lineItemsMiscellaneous === arr) this.poManager.current.lineItemsMiscellaneous = [ ...arr ];
        this.editedItemCopy = null;
    }

    onDeleteLineItem(arr: PurchaseOrderLineItem[] | PurchaseOrderLineItemMiscellaneous[],
                     lineItem: PurchaseOrderLineItem | PurchaseOrderLineItemMiscellaneous): void
    {
        const partType = this.getPartTypeForList(arr, lineItem);
        this.confirmationService.confirm({
            message: 'This line will be deleted permanently. Do you wish to continue?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () =>
            {
                this.busy = true;
                this.poManager.deleteConverterOrPartDirect(partType, lineItem).pipe(finalize(() => this.busy = false)).subscribe(result =>
                {
                    if (result) {
                        arr.splice(arr.findIndex(li => li.id === lineItem.id), 1);
                        this.poManager.current.updateQuantitiesAndPrices();
                    }
                });
            }
        });
    }

    compressedImage: UploadResponse;
    compressImage(): void {
        this.imageCompress.uploadAndGetImageWithMaxSizeAndMetas(12).then((result: any) => {
            this.compressedImage = result;
        }, error => {
            this.compressedImage = error;
        })
    }
    seletedAttachmentType: string;
    attachmentBoxVisible: boolean = false;
    attachmentWait: boolean = false;
    uploadImageSelected(canceled: boolean = false): void
    {
        if(canceled){
            this.seletedAttachmentType = this.attachmentTypes[0].value;
            this.attachmentBoxVisible = false;
            this.compressedImage = undefined;
            return;
        }
        this.attachmentWait = true;

        const arr = this.compressedImage.image.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] ?? 'image/jpeg';
        const bstr = atob(arr[1]);
        const n = bstr.length;
        const u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) {
          u8arr[i] = bstr.charCodeAt(i);
        }
        let file = new File([u8arr], this.compressedImage.fileName, { type: mime });
        this.poManager.uploadImage(this.poManager.current.id, file, this.seletedAttachmentType)
            .subscribe((result: AssignedImageResult) => {
                this.zone.run(() => {
                    this.seletedAttachmentType = this.attachmentTypes[0].value; this.attachmentBoxVisible = false;
                    this.poManager.current.imageUrls.push(result.url);
                    this.messageService.add({
                        severity: 'success',
                        summary: `Purchase Order File`,
                        detail: `The Purchase Order File has been uploaded.`
                    });
                    this.attachmentWait = false;
                    this.attachmentBoxVisible = false;
                    this.compressedImage = null;
                })
            }, (error) => {
                this.log.error(`Cannot upload file: '${file.name} to purchase order '${this.poManager.current.id}'\n${error}'`);

                this.zone.run(() => {
                    this.compressedImage = null;
                    this.attachmentWait = false;
                    const msg = ErrorHandlerService.handleError(this, error, 'uploadImageSelected()', false, false);
                    if (msg.length > 0) this.messageService.add({ severity: 'error', summary: 'PO Image Error', detail: msg });
                })
            });
    }

    onImageClicked(index: number): void
    {
        openBlobUrl(this.poManager.current.imageUrls[index]);
    }

    onImageRemoved(index: number): void
    {
        this.confirmationService.confirm({
            message: 'Are you sure you want to remove the selected image?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () =>
            {
                this.busy = true;
                const url = this.poManager.current.imageUrls[index];
                this.poManager.removeImage(this.poManager.current.id, url)
                    .pipe(finalize(() => { this.busy = false; }))
                    .subscribe(() =>
                    {
                        this.poManager.current.imageUrls = this.poManager.current.imageUrls.filter(i => i !== url);
                        this.messageService.add({
                            severity: 'success',
                            summary: `Purchase Order File`,
                            detail: `The Purchase Order File has been removed.`
                        });
                    }, (error) =>
                    {
                        const msg = ErrorHandlerService.handleError(this, error, 'onImageRemoved()', false, false);
                        if (msg.length > 0) this.messageService.add({ severity: 'error', summary: 'PO Image Error', detail: msg });
                    });
            }
        });
    }
    //endregion

    //region Misc.
    onFilterVendors(e: any){
        this.searchString = this.vendorsFilterValue;
        this.loadVendorsPage(0);
    }
    onClearVendorsFilter(): void {
        this.searchString = undefined;
        this.vendorsFilterValue = undefined
        this.loadVendorsPage(0);
    }
    onLazyLoad(event) {
        if (this.vendors.length >= this.totalItems)
            return;

        const { _, last } = event;
        if (last >= this.vendors.length && !this.vendorListLoading) {
            this.vendorListLoading = true;

            let nextPage = Math.floor(this.vendors.length / this.pageSize);
            this.loadVendorsPage(nextPage)
        }
    }
    loadVendorsPage(page: number): void
    {
        this.poManager.getVendorsForExistingPo(this.authManager.currentUser.locationId, this.authManager.currentUser.isVendorAdmin, {
            pageIndex: page,
            pageSize: this.pageSize,
            searchString: this.searchString
        }).subscribe((vendors) => {
            this.totalItems = vendors.totalCount;
            let items = vendors.items.filter(v => v.id === this.poManager.current.vendorId ||
                     (v.activityStatus !== VendorActivityStatus.Inactive && v.activityStatus !== VendorActivityStatus.NotSelling));

            if(page == 0) 
                this.vendors = items;
            else
                this.vendors = [...this.vendors, ...items];
            this.vendorListLoading = false;
            setTimeout(() => this.selectedVendor = this.vendors.find(x => x.id == this.poManager.current.vendor.id), 100);
        });
    }
    get vendorsLoaded(): boolean {
        if (this.vendors?.length == 0 || (this.vendors?.length == 1 && this.vendors[0].id == this.poManager.current.vendorId)){
            return false;
        }
        return true;
    }
    private setupPrintMenu(): void
    {
        let menuItems = this.isEuLaunch ? cloneDeep(this.allPrintMenuItemsEu) : cloneDeep(this.allPrintMenuItems);
        
        if(this.authManager.currentUser.isGlobalReader)
            menuItems = this.isEuLaunch ? [] : [{ label: 'Create Quote', command: null, items: null, emailToVendor: false, showDetails: true, wordDoc: false, quote: true }];
        
        const po = this.poManager.current;
        this.printMenuItems = po.vendor.email != null && po.vendor.email.length > 0 ? cloneDeep(menuItems)
            : cloneDeep(menuItems).slice(2);
        if (!po.isLocked || this.requiresSignaturePages) menuItems = menuItems.filter(m => !m.wordDoc);
        
        if(po.isLocked) {
            this.printMenuItems = this.printMenuItems.filter(m => m['quote'] == undefined)
        }
        
        if (this.requiresSignaturePages && !this.isEuLaunch)
        {
            this.printMenuItems.forEach(printItem =>
            {
                if(printItem['quote'] == true) 
                {
                    printItem.command = () => this.outputQuote(printItem.showDetails)
                } 
                else 
                {
                    const signatureItems = cloneDeep(this.signaturePageMenuItemsUs);
                    signatureItems.forEach(item => item.command = () =>
                        this.outputPDF(printItem.emailToVendor, printItem.showDetails, printItem.wordDoc, item.type));
                    printItem.items = signatureItems;
                }
            });
        }
        else if (this.requiresSignaturePages && this.isEuLaunch)
        {
            this.printMenuItems.forEach(printItem =>
            {
                printItem.command = () => this.outputPDF(printItem.emailToVendor, printItem.showDetails, printItem.wordDoc, SignaturePageType.Uk)
            });
        }
        else
        {
            this.printMenuItems.forEach(printItem =>
            {
                if(printItem['quote'] == true) 
                {
                    printItem.command = () => this.outputQuote(printItem.showDetails)
                } 
                else 
                {
                    printItem.command = () => this.outputPDF(printItem.emailToVendor, printItem.showDetails, printItem.wordDoc, null);
                    printItem.items = null;
                }
            });
        }
    }

    get requiresSignaturePages(): boolean
    {
        const po = this.poManager.current;

        if(po.signaturePageUrl == null && po.isLocked)
            return false;

        return po.signaturePageUrl == null && po.location != null &&
            !this.authManager.currentUser.isAssayUser && !this.authManager.currentUser.exemptFromVendorLicenseRequirements;
    }

    minTableHeight(list: PurchaseOrderLineItem[] | PurchaseOrderLineItemMiscellaneous[]): number
    {
        return list.length > 1 ? PurchaseOrderComponent.TWO_LINE_TABLE_HEIGHT : PurchaseOrderComponent.ONE_LINE_TABLE_HEIGHT;
    }

    closePO(): void
    {
        this.poManager.retryTransactions().subscribe(result =>
        {
            if (result)
            {
                this.closeView();
                this.poManager.setCurrentPO(null).subscribe();
                return;
            }

            this.confirmationService.confirm({
                message: 'You have changes to the Purchase Order that have not been uploaded.\n\nIf you close this Purchase Order, you will lose those changes permanently.\n\nAre you sure you want to continue to close the Purchase Order?',
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: () =>
                {
                    this.poManager.clearTransactionQueue();
                    this.closeView();
                    this.poManager.setCurrentPO(null);
                }
            });
        });
    }

    createPo(): void {
        this.busy = true;
        this.poManager.createPOFromInventoryList().pipe(finalize(() => this.busy = false)).subscribe({
            next: () => {
                this.messageService.add({severity: 'success', summary: 'Purchase order created'})
                this.closeView()
            },
            error: error => {
                const msg = ErrorHandlerService.handleError(this, error, 'createPo()', false, false);
                if (msg.length > 0) this.messageService.add({ severity: 'error', summary: 'PO Component', detail: msg });
            }
        });
    }
    closeView(): void
    {
        PurchaseOrderComponent.isDisplayed = false;
        this.hide.emit();
    }

    onErrorDismissed(): void
    {
        this.error = null;
    }

    private getPartTypeForList(arr: PurchaseOrderLineItem[] | PurchaseOrderLineItemMiscellaneous[],
                               lineItem: PurchaseOrderLineItem | PurchaseOrderLineItemMiscellaneous): PurchaseOrderTransactionPartType
    {
        return arr === this.poManager.current.lineItemsAllConverters ? (lineItem as PurchaseOrderLineItem).type
            : PurchaseOrderTransactionPartType.Miscellaneous;
    }
    //endregion
}
